import superagentPromise from 'superagent-promise';
import _superagent from 'superagent';
import { appendQueryString } from '../utils';
import OIDCStore from '../../stores/oidcStore';

const superagent = superagentPromise(_superagent, global.Promise);

let API_ROOT = window.location.pathname.slice(0, window.location.pathname.length - 1);

if (process.env.NODE_ENV !== 'production') {
    API_ROOT = 'api';
}

const encode = encodeURIComponent;

const handleErrors = (err, res) => {

    if (err && err.status === 401
        && res.header && res.header['www-authenticate']
        && res.header['www-authenticate'].toLowerCase().includes('bearer')) {
        OIDCStore.reloadPageWithNewToken();
    }

    return err;
};

const setCommonHeaders = commonHeadersInfo => {

    if (!commonHeadersInfo) {
        commonHeadersInfo = {
            apiVersion: '1.0',
            contentType: 'application/json'
        };
    }

    if (!commonHeadersInfo.apiVersion) {
        commonHeadersInfo.apiVersion = '1.0';
    }

    var apiVersionFormatted = `v=${commonHeadersInfo.apiVersion}`;

    if (!commonHeadersInfo.contentType) {
        commonHeadersInfo.contentType = 'application/json';
    }

    return (req) => {
        req.set({
            'Content-Type': `${commonHeadersInfo.contentType};${apiVersionFormatted}`
        });
    };
};

const responseBody = res => res.body;

const responseText = res => res.text;

const createdResourceId = res => res.header.location ? res.header.location.split('/').pop() : res.body;

const tokenPlugin = req => {

    if (OIDCStore.currentUser) {
        req.set('Authorization', `Bearer ${OIDCStore.currentUser.access_token}`);
    }
};

const requests = {
    del: (url, commonHeaders) =>
        superagent
            .del(`${API_ROOT}${url}`)
            .use(tokenPlugin)
            .use(setCommonHeaders(commonHeaders))
            .end(handleErrors)
            .then(responseBody),
    get: (url, responseBodyCallback = responseBody, commonHeaders = false) =>
        superagent
            .get(`${API_ROOT}${url}`)
            .use(tokenPlugin)
            .use(setCommonHeaders(commonHeaders))
            .end(handleErrors)
            .then(responseBodyCallback),
    put: (url, body, commonHeaders) =>
        superagent
            .put(`${API_ROOT}${url}`, body)
            .use(tokenPlugin)
            .use(setCommonHeaders(commonHeaders))
            .end(handleErrors)
            .then(responseBody),
    post: (url, body, commonHeaders) =>
        superagent
            .post(`${API_ROOT}${url}`, body)
            .use(setCommonHeaders(commonHeaders))
            .use(tokenPlugin)
            .end(handleErrors)
            .then(createdResourceId),
    getExternal: (url) =>
        superagent
            .get(url)
            .use(tokenPlugin)
            .use(setCommonHeaders(false))
            .end(handleErrors)
};

const Auth = {
    login: (userName, password, providerId, tenant, isAcceptedEula, origin, authenticationCode, isPasswordValidated, recoveryCode, rememberTwoFABrowser, accessIsFromACUI) =>
        requests.post('/identity/login', { userName, password, providerId, tenant, isAcceptedEula, origin, authenticationCode, isPasswordValidated, recoveryCode, rememberTwoFABrowser, accessIsFromACUI })
};

const PasswordPolicies = {
    getPasswordPolicies: () =>
        requests.get(`/PasswordPolicies/`),

    getPasswordPoliciesForTenant: (tenant) =>
        requests.get(`/PasswordPolicies?tenant=${tenant}`),

    setPasswordPolicies: (minPasswordLength, minNoOfDigits, minNoOfLowercaseChars, minNoOfUppercaseChars, minNoOfSpecialChars, passwordExpiryDays, passwordHistoryCount, showForgotPasswordLink) =>
        requests.put(`/PasswordPolicies/`, { minPasswordLength, minNoOfDigits, minNoOfLowercaseChars, minNoOfUppercaseChars, minNoOfSpecialChars, passwordExpiryDays, passwordHistoryCount, showForgotPasswordLink }),

    getLockoutOptions: () =>
        requests.get(`/PasswordPolicies/LockoutOptions/`),

    getLockoutOptionsForTenant: (tenant) =>
        requests.get(`/PasswordPolicies/LockoutOptions?tenant=${tenant}`),

    setLockoutOptions: (maxFailedAccessAttempts, defaultLockoutTimeSpan) =>
        requests.put(`/PasswordPolicies/LockoutOptions/`, { maxFailedAccessAttempts, defaultLockoutTimeSpan })

};

const TwoFactorAuth = {
    getAuthenticatorKeyAndCode: () =>
        requests.get(`/TwoFactorAuthentication/GetAuthenticatorKey`),

    enableTwoFactorAuth: (twoFactorCode) =>
        requests.post(`/TwoFactorAuthentication/EnableTwoFactor`, { twoFactorCode }),

    disableTwoFactorAuth: () =>
        requests.post(`/TwoFactorAuthentication/DisableTwoFactor`),

    verifyCode: (code) =>
        requests.post(`/TwoFactorAuthentication/verifyCode?twoFactorCode=${code}`),

    getRecoveryCodes: () =>
        requests.get('/TwoFactorAuthentication/GenerateRecoveryCodes'),

    getTwoFactorStatus: () =>
        requests.get('/TwoFactorAuthentication/TwoFactorStatus'),

    resetAuthenticatorKey: () =>
        requests.post(`/TwoFactorAuthentication/ResetAuthenticatorKey`),

    forgetMe: () =>
        requests.post(`/TwoFactorAuthentication/ForgetMe`),

    isClientRemembered: () =>
        requests.post(`/TwoFactorAuthentication/IsClientRemembered`)
};

const TenantConfiguration = {
    getTwoFactorAuthSettings: () =>
        requests.get(`/TwoFactorAuthSettings`),

    setTwoFactorAuthSettings: (twoFAEnforced, recoveryCodeCount) =>
        requests.put(`/TwoFactorAuthSettings`, { twoFAEnforced, recoveryCodeCount }),

    setForgotPasswordLinkSetting: (displayForgotPasswordLink) =>
        requests.put(`/ForgotPasswordLinkSetting`, displayForgotPasswordLink),

    getForgotPasswordSettings: () =>
        requests.get(`/ForgotPasswordLinkSetting`),

    getForgotPasswordSettingsForTenant: (tenant) =>
        requests.get(`/ForgotPasswordLinkSetting?tenant=${tenant}`),

    getLoginSettings: () =>
        requests.get(`/LoginSettings`),

    getDefaultLoginMethod: () =>
        requests.get(`/DefaultLoginMethod`),

    setLoginSettings: (credentialsButton, defaultLoginMethod) =>
        requests.put(`/LoginSettings`, { credentialsButton, defaultLoginMethod }),

    getUserInactivity: () =>
        requests.get(`/UserInactivity`),

    setUserInactivity: (userInactivity) =>
        requests.put(`/UserInactivity`, { userInactivity })
};

const Configurations = {
    getConfigurations: () =>
        requests.get('/Configurations')
};

const AuthenticationProviders = {
    getAllAuthenticationProviders: () =>
        requests.get(`/authenticationproviders`),

    getAllAuthenticationProvidersOfTenant: (tenant) =>
        requests.get(`/authenticationproviders/${tenant}`)
};

const Users = {
    getAllUsers: () =>
        requests.get(`/users`),
    getUser: (userId) =>
        requests.get(`/users/${userId}`),
    createUser: (user) =>
        requests.post('/users', user),
    inviteUsers: (user) =>
        requests.post('/users/inviteUsers', user),
    deleteUser: (userId) =>
        requests.del(`/users/${userId}`),
    updateUser: (userId, user) =>
        requests.put(`/users/${userId}`, user),
    updateUserExemption: (id, twoFAExemption) =>
        requests.put(`/users/UpdateUserExemption`, { id, twoFAExemption }),
    updateUserAccessToUi: (id, accessToUi) =>
        requests.put(`/users/UpdateUserAccessToUi`, { id, accessToUi }),
    firstAdminExistence: () =>
        requests.get('/users/firstAdminExistence'),
    createFirstAdmin: (admin) =>
        requests.post('/users/firstadmin', admin),
    forgotPassword: (username, tenant) =>
        requests.post('/users/forgotpassword', { username, tenant }),
    resetPassword: (username, tenant, password, token) =>
        requests.post('/users/resetpassword', { username, tenant, password, token }),
    resetPasswordByManager: (userId) =>
        requests.post(`/users/${userId}/resetpassword`),
    generatePassword: () =>
        requests.post(`/users/generatePassword`),
    changePassword: (username, tenant, oldPassword, newPassword) =>
        requests.post('/users/changepassword', { username, tenant, oldPassword, newPassword }),
    changeMyPassword: (oldPassword, newPassword) =>
        requests.post('/users/changemypassword', { oldPassword, newPassword }),
    bulkDelete: (userIds) =>
        requests.post('/users/bulkDelete', userIds),
    bulkUpdateStatus: (active, usersIds) =>
        requests.post('/users/bulkUpdateStatus', { active, usersIds }),
    bulkEditRoles: (addRemoveRolesModel) =>
        requests.post('/users/bulkAddRemoveUsersRoles', addRemoveRolesModel)
};

const MyProfile = {
    get: () =>
        requests.get(`/myprofile`),
    update: (userProfile) =>
        requests.put('/myprofile', userProfile)
};

const Teams = {
    getAllTeams: () =>
        requests.get(`/teams`),
    addTeam: (team) =>
        requests.post(`/teams`, team),
    updateTeam: (teamId, team) =>
        requests.put(`/teams/${teamId}`, team),
    deleteTeam: (teamId) =>
        requests.del(`/teams/${teamId}`)
};

const Roles = {
    getAllRoles: () =>
        requests.get('/roles'),
    getRole: id =>
        requests.get(`/roles/${id}`),
    createRole: (role) =>
        requests.post(`/roles`, role),
    updateRole: (id, role) =>
        requests.put(`/roles/${id}`, role),
    deleteRole: id =>
        requests.del(`/roles/${id}`)
};

const Permissions = {
    getAllPermissions: () =>
        requests.get('/permissions')
};


const OidcErrors = {
    getOidcError: (errorId) =>
        requests.get(`/oidcErrors/${errorId}`)
};

const SystemLocales = {
    getSystemLocales: () =>
        requests.get('/systemlocales')
};

const SmtpSettings = {
    getSmtpSettings: () =>
        requests.get(`/smtpsettings`),
    createSmtpSettings: (smtpSettings) =>
        requests.post('/smtpsettings', smtpSettings),
    updateSmtpSettings: (smtpSettingsId, smtpSettings) =>
        requests.put(`/smtpsettings/${smtpSettingsId}`, smtpSettings),
    testConnection: (smtpSettings) =>
        requests.post('/smtpsettings/testconnection', smtpSettings)
};

const TeamMembers = {
    getTeamUsers: (teamId) =>
        requests.get(`/teams/${teamId}/users`),
    putTeamUsers: (teamId, userIds) =>
        requests.put(`/teams/${teamId}/users`, userIds),
    removeTeamUser: (teamId, userId) =>
        requests.del(`/teams/${teamId}/users/${userId}`)
};

const LDAPServers = {
    getLDAPServers: () =>
        requests.get(`/ldapservers`),
    getLDAPServer: (serverId) =>
        requests.get(`/ldapservers/${serverId}`),
    createLDAPServer: (ldapServer) =>
        requests.post(`/ldapservers`, ldapServer),
    updateLDAPServer: (serverId, ldapServer) =>
        requests.put(`/ldapservers/${serverId}`, ldapServer),
    deleteLDAPServer: (serverId) =>
        requests.del(`/ldapservers/${serverId}`),
    testLDAPServerConnection: (ldapServer) =>
        requests.post(`/ldapservers/testconnection`, ldapServer),
    searchGroupEntries: (serverId, nameContainsPattern) =>
        requests.get(`/ldapservers/${serverId}/groupentries?namecontainspattern=${nameContainsPattern}`)
};

const LDAPTeamMappings = {
    get: (params) => {

        let url = appendQueryString('/ldapteammappings')('ldapServerId', params.ldapServerId);
        url = appendQueryString(url)('teamId', params.teamId);

        return requests.get(url);
    },
    put: (ldapServerId, data) =>
        requests.put(`/ldapservers/${ldapServerId}/teammappings`, data),
    delete: (ldapTeamMappingId) =>
        requests.del(`/ldapteammappings/${ldapTeamMappingId}`)
};

const LDAPRoleMappings = {
    getRoleMappingsByLdapServerId: (ldapServerId) =>
        requests.get(`/ldaprolemappings?ldapserverid=${ldapServerId}`),
    putLdapServerRoleMappings: (ldapServerId, roleMappings) =>
        requests.put(`/LDAPServers/${ldapServerId}/rolemappings`, roleMappings)
};

const LDAPUserEntries = {
    get: (serverId, userNameContainsPattern) =>
        requests.get(`/ldapservers/${serverId}/userentries?usernamecontainspattern=${userNameContainsPattern}`)
};

const WindowsDomains = {
    getAllDomains: () =>
        requests.get('/windowsdomains'),
    addDomain: (domain) =>
        requests.post('/windowsdomains/', domain),
    editDomain: (id, domain) =>
        requests.put(`/windowsdomains/${id}`, domain),
    deleteDomain: (id) =>
        requests.del(`/windowsdomains/${id}`)
};

const DomainUserEntries = {
    get: (domainId, containsPattern) =>
        requests.get(`/windowsdomains/${domainId}/userentries?containspattern=${containsPattern}`)
};

const sharedSaveIdentityProvidersForm = (superAgent, form) =>
    superAgent
        .use(tokenPlugin)
        .attach('certificateFile', form.certificateFile, form.certificateFileName)
        .field('active', form.active)
        .field('name', form.name)
        .field('issuer', form.issuer)
        .field('loginUrl', form.loginUrl)
        .field('logoutUrl', form.logoutUrl)
        .field('errorUrl', form.errorUrl)
        .field('signAuthnRequest', form.signAuthnRequest)
        .field('authnRequestBinding', form.authnRequestBinding)
        .field('isManualManagement', form.isManualManagement)
        .field('defaultTeamId', form.defaultTeamId)
        .field('defaultRoleId', form.defaultRoleId)
        .field('mapSamlIdpAttributes', form.mapSamlIdpAttributes)
        .field('roleMappingAttribute', form.roleMappingAttribute)
        .field('teamMappingAttribute', form.teamMappingAttribute)

        .then(responseBody);

const SamlIdentityProviders = {
    getAllSAMLIdentityProviders: () =>
        requests.get('/samlidentityproviders'),
    getSAMLIdentityProvider: idpId =>
        requests.get(`/samlidentityproviders/${idpId}`),
    deleteSAMLIdentityProvider: idpId =>
        requests.del(`/samlidentityproviders/${idpId}`),
    post: form =>
        sharedSaveIdentityProvidersForm(superagent.post(`${API_ROOT}/samlidentityproviders`), form),
    put: (idpId, form) =>
        sharedSaveIdentityProvidersForm(superagent.put(`${API_ROOT}/samlidentityproviders/${idpId}`), form)
};


const SamlServiceProvider = {
    getMetadata: () =>
        requests.get('/samlserviceprovider/metadata', responseText),
    get: () =>
        requests.get('/samlserviceprovider'),
    put: (form) =>
        superagent.put(`${API_ROOT}/samlserviceprovider`)
            .use(tokenPlugin)
            .attach('certificateFile', form.certificateFile, form.certificateFileName)
            .field('certificatePassword', form.certificatePassword)
            .field('issuer', form.issuer)
};

const ServiceProviders = {
    getServiceProviders: () =>
        requests.get('/serviceproviders')
};

const SessionTimeout = {
    getSessionTimeout: () =>
        requests.get('/sessionTimeout')
};


const MasterAccessControl = {
    getMasterAccessControl: () => requests.get('/primaryAccessControl'),
    updateMasterAccessControl: (localURL, name, issuer, jwks, active, discovery) => {
        const masterAccessControl = {
            name: name,
            issuer: issuer,
            localUrl: localURL,
            isActive: active,
            jwks: jwks,
            Discovery: discovery
        };
        return requests.put(`/primaryAccessControl`, masterAccessControl);
    }
};

const OpenIDConfiguration = {
    getConfigurations: (url) =>
        requests.getExternal(`${url}/identity/.well-known/openid-configuration`),
    getJWKS: (url) => requests.getExternal(url)
};


export default {
    Auth,
    Configurations,
    Users,
    AuthenticationProviders,
    Teams,
    Roles,
    OidcErrors,
    SystemLocales,
    SmtpSettings,
    MyProfile,
    Permissions,
    TeamMembers,
    LDAPServers,
    LDAPTeamMappings,
    LDAPRoleMappings,
    LDAPUserEntries,
    WindowsDomains,
    DomainUserEntries,
    SamlIdentityProviders,
    SamlServiceProvider,
    ServiceProviders,
    SessionTimeout,
    MasterAccessControl,
    OpenIDConfiguration,
    TwoFactorAuth,
    PasswordPolicies,
    TenantConfiguration
};
